import type { RunPreparationStage, RunPreparationSummary } from "@nova-ai/shared"
import { Check, X } from "lucide-react"

import { humanizeEnumLabel } from "../../lib/enum-label"
import { formatDateTime, formatRelativeTime } from "../../lib/format-date"
import { cn } from "../../lib/utils"
import { Callout } from "../ui/callout"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"

const PREPARATION_STATUS: Record<
  RunPreparationSummary["status"],
  { label: string; tone: BadgeTone }
> = {
  queued: { label: "Preparation queued", tone: "muted" },
  running: { label: "Preparing", tone: "info" },
  blocked: { label: "Preparation blocked", tone: "warning" },
  ready: { label: "Ready for review", tone: "success" },
  disabled: { label: "Preparation paused", tone: "muted" },
}

type StepState = "done" | "active" | "blocked" | "pending"

/** Preparation progress stays separate from the run's lifecycle and approval.
 *  Input fingerprints and evidence ids are machine bookkeeping; they stay in
 *  the API and CLI, not here. */
export function RunPreparationStatus({
  preparation,
}: {
  preparation: RunPreparationSummary | null | undefined
}) {
  if (!preparation) {
    return null
  }
  const status = PREPARATION_STATUS[preparation.status]
  const done = new Set(preparation.completed.map((completion) => completion.stage))

  return (
    <section aria-label="Automatic preparation" className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>

        <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
          {preparation.plan.map((stage, index) => (
            <li key={stage} className="flex items-center gap-1.5">
              {index > 0 && <span className="text-muted-foreground/30">&middot;</span>}

              <PreparationStep stage={stage} state={stepState(stage, preparation, done)} />
            </li>
          ))}
        </ol>
      </div>

      <p className="text-muted-foreground">
        {`Input revision ${preparation.requestedRevision}. `}

        {preparation.oldestUnresolvedAt ? (
          <span title={formatDateTime(preparation.oldestUnresolvedAt)}>
            {`Oldest unresolved change: ${formatRelativeTime(preparation.oldestUnresolvedAt)}.`}
          </span>
        ) : null}

        {!preparation.oldestUnresolvedAt &&
          preparation.status !== "ready" &&
          "Original detection time is unknown."}
      </p>

      {(preparation.uncoveredOutsideMembershipCount ?? 0) > 0 && (
        <p className="text-warning">{`${preparation.uncoveredOutsideMembershipCount} historical change events remain outside this artifact's active membership.`}</p>
      )}

      {preparation.blocker && (
        <Callout dense tone="warning">
          <p className="break-words text-sm text-foreground">{preparation.blocker}</p>

          {preparation.attempts > 1 && (
            <p className="mt-0.5 text-muted-foreground tabular-nums">{`Tried ${preparation.attempts} times`}</p>
          )}
        </Callout>
      )}

      {preparation.status === "ready" && (
        <p className="text-muted-foreground">
          {preparation.artifactId !== null && (
            <span className="tabular-nums text-foreground">{`Review artifact #${preparation.artifactId}. `}</span>
          )}
          Human approval is required before submission.
        </p>
      )}
    </section>
  )
}

function PreparationStep({ stage, state }: { stage: RunPreparationStage; state: StepState }) {
  return (
    <span
      data-state={state}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap",
        state === "done" && "text-foreground",
        state === "active" && "font-medium text-info",
        state === "blocked" && "font-medium text-warning",
        state === "pending" && "text-muted-foreground"
      )}
    >
      {state === "done" && <Check aria-hidden className="size-3 text-success" />}

      {state === "blocked" && <X aria-hidden className="size-3" />}

      {humanizeEnumLabel(stage)}
    </span>
  )
}

function stepState(
  stage: RunPreparationStage,
  preparation: RunPreparationSummary,
  done: ReadonlySet<RunPreparationStage>
): StepState {
  if (preparation.status === "ready") {
    return "done"
  }
  if (stage === preparation.stage && preparation.status === "blocked") {
    return "blocked"
  }
  if (stage === preparation.stage && preparation.status === "running") {
    return "active"
  }
  return done.has(stage) ? "done" : "pending"
}
